import { useState } from "react"
import Icon from "@/v2-ui/icon"
import Button from "@/v2-backoffice-client/components/button"
import Link from "@/v2-backoffice-client/components/link"
import { FENERUM_ACCOUNT_BASE_URL } from "@/v2-backoffice-client/constants"
import { CUSTOMER_VIEW_TAB_PARAM } from "@/v2-backoffice-client/customer/customer.links"
import {
  CUSTOMER_SEAT_STATUSES,
  deriveCustomerSeatFacts,
} from "@/v2-backoffice-client/customer/license/customer.license.utils"
import LicenseSeatsDialog, {
  getLicenseSeatsDialogTitle,
} from "@/v2-backoffice-client/license/seats/LicenseSeatsDialog"
import {
  LICENSE_LIVE_STATUS_CODES,
  LICENSE_SOURCE_OPERATOR,
} from "@/v2-backoffice-client/license/license.constants"
import { describeSeatUpsellPath } from "@/v2-backoffice-client/license/license.utils"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import type { LicenseFragment } from "@/v2-backoffice-client/license/__types__/license.fragment"

export type CustomerSeatOveruseNoticeProps = {
  customer: CustomerFragment
  /** The customer's licenses (the panel already holds them); the capped live ones get a seat-cap button. */
  licenses: LicenseFragment[] | undefined
}

// The seat warning on the customer panel's Licensing tab: shown only while
// the customer occupies more seats than its live licenses allow. Seats are
// reported, never enforced (FUS-923: invitations go through, the customer
// is told seats beyond the license may be invoiced), so the sales owner
// has to see the overrun and close it — this notice says what is going on
// in one line and puts the three ways out next to it:
//
//   * the real fix is the seat count where the license comes from: Fenerum
//     for a billing-derived license (the mirror picks the new quantity up
//     within the hour), the cap itself for an operator grant;
//   * an override on a billing-derived license is for a negotiated
//     exception (a chain agreement), not a correction — the dialog says so;
//   * people who left are removed on the Users tab.
//
// Renders nothing when the customer is within its cap, uncapped or
// unlicensed, so the caller mounts it unconditionally.
function CustomerSeatOveruseNotice(props: CustomerSeatOveruseNoticeProps) {
  const { customer, licenses } = props
  const facts = deriveCustomerSeatFacts(customer)
  if (
    facts.status !== CUSTOMER_SEAT_STATUSES.OVER_LIMIT ||
    facts.licensedSeats == null
  ) {
    return null
  }

  const over = facts.seatsUsed - facts.licensedSeats
  const cappedLicenses = (licenses ?? []).filter(
    (license) =>
      LICENSE_LIVE_STATUS_CODES.includes(license.LicenseStatusCode) &&
      license.Seats != null,
  )
  const billingLicenses = cappedLicenses.filter(
    (license) => license.LicenseSourceCode !== LICENSE_SOURCE_OPERATOR,
  )
  const hasBillingLicense = billingLicenses.length > 0
  // Where the extra seats are actually sold, per billing license: the seat
  // line its plan names (raise that quantity), the next size when it is a
  // seat bundle, or the base subscription's own quantity. Named so the CSM
  // changes the right thing in Fenerum.
  const seatLineNames = [
    ...new Set(
      billingLicenses
        .map((license) => license.SeatLinePlanName)
        .filter((name): name is string => !!name),
    ),
  ]
  const isBundleOnly =
    hasBillingLicense &&
    billingLicenses.every(
      (license) => !license.SeatLinePlanName && !!license.SeatsIncluded,
    )
  const seatLineHint = [
    ...new Set(
      billingLicenses.map((license) => describeSeatUpsellPath(license)),
    ),
  ].join(" ")

  return (
    <section
      role="status"
      className="flex flex-col gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2.5 text-sm"
      data-feature-code="UI_BackofficeCustomer_SeatOveruse_Notice"
    >
      <p className="flex items-start gap-2">
        <Icon
          src="triangle-warning"
          size={14}
          className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-300"
          aria-hidden
        />
        <span>
          <span className="font-medium">
            Over the seat cap: {facts.seatsUsed} seats in use on{" "}
            {facts.licensedSeats} licensed ({over} over).
          </span>{" "}
          <span className="text-muted-foreground">
            {facts.userCount} users plus {facts.pendingInvitationCount} open
            invitations. Nothing is blocked: invitations keep going through and
            the customer is told that seats beyond the license may be invoiced.
            Agree the seat count with the customer, then set it where the
            license comes from.
          </span>
        </span>
      </p>

      <div className="flex flex-wrap items-center gap-2 pl-6">
        {hasBillingLicense && customer.BillingAccountUuid && (
          <Link
            variant="unstyled"
            iconSrc="bank"
            iconSize={12}
            external
            to={`${FENERUM_ACCOUNT_BASE_URL}/${customer.BillingAccountUuid}/`}
            className="inline-flex items-center gap-1.5 rounded border border-border bg-background px-2 py-1 text-xs font-medium hover:bg-muted"
            title={seatLineHint}
            data-feature-code="UI_BackofficeCustomer_SeatOveruse_Fenerum"
          >
            {seatLineNames.length === 1
              ? `Add seats in Fenerum (${seatLineNames[0]})`
              : isBundleOnly
                ? "Move to the next size in Fenerum"
                : "Add seats in Fenerum"}
          </Link>
        )}

        {cappedLicenses.map((license) => (
          <SeatCapButton
            key={license.Id}
            license={license}
            showLicenseName={cappedLicenses.length > 1}
          />
        ))}

        <Link
          variant="unstyled"
          iconSrc="users"
          iconSize={12}
          to={`?${CUSTOMER_VIEW_TAB_PARAM}=users`}
          className="inline-flex items-center gap-1.5 rounded border border-border bg-background px-2 py-1 text-xs font-medium hover:bg-muted"
          title="People who have left still hold a seat until they are removed."
          data-feature-code="UI_BackofficeCustomer_SeatOveruse_Users"
        >
          Review users
        </Link>
      </div>
    </section>
  )
}

type SeatCapButtonProps = {
  license: LicenseFragment
  showLicenseName: boolean
}

// One button per capped live license, opening the same seat-cap dialog as
// the license row's menu: "Edit Seat Cap" on an operator grant, "Override
// Seat Cap" on a billing-derived license (the dialog explains the
// difference and shows what billing says).
function SeatCapButton(props: SeatCapButtonProps) {
  const { license, showLicenseName } = props
  const [isOpen, setIsOpen] = useState(false)
  const title = getLicenseSeatsDialogTitle(license)

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="xs"
        iconSrc="users-alt"
        data-feature-code="UI_BackofficeCustomer_SeatOveruse_SeatCap"
        onClick={() => {
          setIsOpen(true)
        }}
      >
        {showLicenseName
          ? `${title}: ${license.PlanName ?? license.ProductName}`
          : title}
      </Button>

      {isOpen && (
        <LicenseSeatsDialog
          license={license}
          open={isOpen}
          onOpenChange={setIsOpen}
        />
      )}
    </>
  )
}

export default CustomerSeatOveruseNotice
